/**
 * R8A-3 OTK option catalog form write contract (§11).
 * Explicit allowlisted FormData — never for…in / Object.keys over Formik values.
 */

export const OTK_OPTION_CATALOG_MUTATIONS = Object.freeze({
  CREATED: 'created',
  UPDATED: 'updated',
  NOOP: 'noop',
});

export const OTK_OPTION_CATALOG_WRITE_ALLOWLISTS = Object.freeze({
  groups: Object.freeze({
    create: Object.freeze(['name']),
    update: Object.freeze(['name']),
  }),
  preventions: Object.freeze({
    create: Object.freeze(['name']),
    update: Object.freeze(['name']),
  }),
  measure_complexities: Object.freeze({
    create: Object.freeze(['category', 'description', 'time_in_minutes']),
    update: Object.freeze(['category', 'description', 'time_in_minutes']),
  }),
  root_causes: Object.freeze({
    create: Object.freeze(['name']),
    update: Object.freeze(['name']),
  }),
  protocol_types: Object.freeze({
    create: Object.freeze(['name']),
    update: Object.freeze(['name']),
  }),
  error_types: Object.freeze({
    create: Object.freeze(['name']),
    update: Object.freeze(['name']),
  }),
});

/** Identity / audit keys that must never be submitted from editors. */
export const OTK_OPTION_CATALOG_FORBIDDEN_WRITE_KEYS = Object.freeze([
  'id',
  'defined_id',
  'created_at',
  'updated_at',
  'created_by_user_id',
  'updated_by_user_id',
  'created_by_user',
  'updated_by_user',
]);

export function normalizeLocalizedJsonField(value) {
  if (value == null) {
    return { sk: '', de: '', en: '' };
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        return {
          sk: parsed.sk ?? '',
          de: parsed.de ?? '',
          en: parsed.en ?? '',
        };
      }
    } catch (_e) {
      // plain string fallback
    }
    return { sk: value, de: '', en: '' };
  }
  if (typeof value === 'object' && !Array.isArray(value)) {
    return {
      sk: value.sk ?? '',
      de: value.de ?? '',
      en: value.en ?? '',
    };
  }
  return { sk: '', de: '', en: '' };
}

function appendLocalizedJson(formData, field, value) {
  formData.append(field, JSON.stringify(normalizeLocalizedJsonField(value)));
}

/**
 * Build multipart write payload for one R8A resource.
 * @param {'groups'|'preventions'|'measure_complexities'|'root_causes'|'protocol_types'|'error_types'} resource
 * @param {object} values Formik values
 * @returns {FormData}
 */
export function buildOtkOptionCatalogWritePayload(resource, values) {
  const formData = new FormData();
  const v = values && typeof values === 'object' ? values : {};

  switch (resource) {
    case 'groups':
    case 'preventions':
    case 'root_causes':
    case 'protocol_types':
    case 'error_types':
      appendLocalizedJson(formData, 'name', v.name);
      break;
    case 'measure_complexities':
      formData.append('category', v.category ?? '');
      appendLocalizedJson(formData, 'description', v.description);
      // Preserve numeric 0 — never coerce via || ''
      formData.append(
        'time_in_minutes',
        v.time_in_minutes === null || v.time_in_minutes === undefined
          ? ''
          : String(v.time_in_minutes)
      );
      break;
    default:
      throw new Error(`Unknown OTK option catalog resource: ${resource}`);
  }

  return formData;
}

/** FormData keys present on a built payload (for tests). */
export function listFormDataKeys(formData) {
  return [...formData.keys()];
}

/**
 * Initial Formik values. Copy mode (entity + type=add) keeps localized domain
 * fields and drops identity/audit so create payload cannot spoof them via UI state.
 */
export function initialValuesForOtkOptionCatalogForm(resource, entity, type) {
  if (entity == null) {
    if (resource === 'measure_complexities') {
      return {
        category: '',
        description: { sk: '', de: '', en: '' },
        time_in_minutes: '',
      };
    }
    return { name: { sk: '', de: '', en: '' } };
  }

  const canonical = formValuesFromOtkOptionCatalogRow(resource, entity);
  if (type === 'add') {
    if (resource === 'measure_complexities') {
      return {
        category: canonical.category ?? '',
        description: normalizeLocalizedJsonField(canonical.description),
        time_in_minutes:
          canonical.time_in_minutes === null || canonical.time_in_minutes === undefined
            ? ''
            : canonical.time_in_minutes,
      };
    }
    return {
      name: normalizeLocalizedJsonField(canonical.name),
    };
  }
  return canonical;
}

export function formValuesFromOtkOptionCatalogRow(resource, row) {
  if (!row || typeof row !== 'object') return null;
  if (resource === 'measure_complexities') {
    return {
      ...row,
      category: row.category ?? '',
      description: normalizeLocalizedJsonField(row.description),
      time_in_minutes:
        row.time_in_minutes === null || row.time_in_minutes === undefined
          ? ''
          : row.time_in_minutes,
    };
  }
  return {
    ...row,
    name: normalizeLocalizedJsonField(row.name),
  };
}

/**
 * Classify R8A-1b write response. Body status ≥400 → error.
 * Missing/unknown mutation on otherwise-success envelope → unknown (no silent update).
 */
export function classifyOtkOptionCatalogWriteResponse(response) {
  if (response == null || typeof response !== 'object') {
    return { kind: 'unknown', mutation: null };
  }
  const http =
    typeof response.__httpStatus === 'number' ? response.__httpStatus : null;
  const bodyStatus = typeof response.status === 'number' ? response.status : null;
  if ((http != null && http >= 400) || (bodyStatus != null && bodyStatus >= 400)) {
    return { kind: 'error', mutation: null };
  }
  const mutation = response.mutation;
  if (
    mutation === OTK_OPTION_CATALOG_MUTATIONS.CREATED ||
    mutation === OTK_OPTION_CATALOG_MUTATIONS.UPDATED ||
    mutation === OTK_OPTION_CATALOG_MUTATIONS.NOOP
  ) {
    return { kind: mutation, mutation };
  }
  return { kind: 'unknown', mutation: mutation ?? null };
}

/**
 * Pure save-outcome planner used by editors + tests.
 * @returns {{
 *   outcome: 'created'|'updated'|'noop'|'error'|'unknown',
 *   shouldToast: boolean,
 *   shouldEmit: boolean,
 *   shouldClose: boolean,
 *   emitAction: 'created'|'updated'|null,
 *   catalogRefreshGetCount: number,
 *   canonicalValues: object|null,
 * }}
 */
export function planOtkOptionCatalogWriteOutcome({
  response,
  resource,
  closeAfter = false,
}) {
  const classified = classifyOtkOptionCatalogWriteResponse(response);
  const row = response?.data ?? null;
  const canonical = row
    ? formValuesFromOtkOptionCatalogRow(resource, row)
    : null;

  if (
    classified.kind === OTK_OPTION_CATALOG_MUTATIONS.CREATED ||
    classified.kind === OTK_OPTION_CATALOG_MUTATIONS.UPDATED
  ) {
    return {
      outcome: classified.kind,
      shouldToast: true,
      shouldEmit: true,
      shouldClose: Boolean(closeAfter),
      emitAction: classified.kind,
      catalogRefreshGetCount: 1,
      canonicalValues: canonical,
    };
  }

  if (classified.kind === OTK_OPTION_CATALOG_MUTATIONS.NOOP) {
    return {
      outcome: 'noop',
      shouldToast: false,
      shouldEmit: false,
      shouldClose: Boolean(closeAfter),
      emitAction: null,
      catalogRefreshGetCount: 0,
      canonicalValues: canonical,
    };
  }

  return {
    outcome: classified.kind,
    shouldToast: false,
    shouldEmit: false,
    shouldClose: false,
    emitAction: null,
    catalogRefreshGetCount: 0,
    canonicalValues: null,
  };
}

/** Request-count matrix helper for contract tests (no network). */
export function simulateOtkOptionCatalogMutationNetworkCounts({
  mutation,
  closeAfter = false,
  matchingList = true,
}) {
  const response =
    mutation === 'error'
      ? { status: 400, message: 'fail' }
      : mutation === 'unknown'
        ? { status: 200, data: { id: 1 } }
        : {
            status: mutation === 'created' ? 201 : 200,
            mutation,
            data: { id: 42, name: { sk: 'X', de: '', en: '' } },
          };

  const plan = planOtkOptionCatalogWriteOutcome({
    response,
    resource: 'groups',
    closeAfter,
  });

  return {
    write: 1,
    catalogGET: plan.shouldEmit && matchingList ? 1 : 0,
    toast: plan.shouldToast,
    emit: plan.shouldEmit,
    close: plan.shouldClose,
    outcome: plan.outcome,
  };
}
